import { dateTime, money } from '../lib/format';
import type { Order } from '../types';

/**
 * Printable 80mm-style receipt. Purely presentational — it renders the
 * server-computed values from the Order DTO. No bill math is done here.
 */
export function Receipt({
  order,
  footer,
  gstin,
}: {
  order: Order;
  footer?: string;
  gstin?: string | null;
}) {
  return (
    <div className="receipt print-area">
      <div className="center">
        <div className="r-title">1885 NY SMASH BURGERS</div>
        <div>{order.storeName}</div>
        {gstin && <div>GSTIN: {gstin}</div>}
      </div>
      <hr />
      <div className="r-row">
        <span>Bill</span>
        <strong>{order.billNumber}</strong>
      </div>
      <div className="r-row">
        <span>Date</span>
        <span>{dateTime(order.createdAt)}</span>
      </div>
      <div className="r-row">
        <span>Cashier</span>
        <span>{order.cashierName}</span>
      </div>
      <div className="r-row">
        <span>Type</span>
        <span>{order.orderType.replace('_', '-')}</span>
      </div>
      {order.tableNumber && (
        <div className="r-row">
          <span>Table</span>
          <span>{order.tableNumber}</span>
        </div>
      )}
      {order.customerMobile && (
        <div className="r-row">
          <span>Mobile</span>
          <span>{order.customerMobile}</span>
        </div>
      )}
      <hr />
      <table>
        <thead>
          <tr>
            <td>Item</td>
            <td style={{ textAlign: 'center' }}>Qty</td>
            <td style={{ textAlign: 'right' }}>Amt</td>
          </tr>
        </thead>
        <tbody>
          {order.items.map((it, i) => (
            <tr key={i}>
              <td>
                {it.productName}
                <br />
                <span style={{ fontSize: 11 }}>@ {money(it.unitPrice)}</span>
              </td>
              <td style={{ textAlign: 'center' }}>{it.quantity}</td>
              <td style={{ textAlign: 'right' }}>{money(it.lineTotal)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <hr />
      <div className="r-row">
        <span>Subtotal</span>
        <span>{money(order.subtotal)}</span>
      </div>
      {order.discountAmount > 0 && (
        <div className="r-row">
          <span>Discount</span>
          <span>- {money(order.discountAmount)}</span>
        </div>
      )}
      <div className="r-row">
        <span>Taxable</span>
        <span>{money(order.taxableAmount)}</span>
      </div>
      <div className="r-row">
        <span>CGST ({order.cgstRate}%)</span>
        <span>{money(order.cgstAmount)}</span>
      </div>
      <div className="r-row">
        <span>SGST ({order.sgstRate}%)</span>
        <span>{money(order.sgstAmount)}</span>
      </div>
      <hr />
      <div className="r-row r-total">
        <span>TOTAL</span>
        <span>{money(order.grandTotal)}</span>
      </div>
      <div className="r-row">
        <span>Payment</span>
        <span>{order.paymentMethod}</span>
      </div>
      {order.cashReceived != null && (
        <div className="r-row">
          <span>Cash</span>
          <span>{money(order.cashReceived)}</span>
        </div>
      )}
      {order.changeReturned != null && (
        <div className="r-row">
          <span>Change</span>
          <span>{money(order.changeReturned)}</span>
        </div>
      )}
      {order.status === 'CANCELLED' && (
        <>
          <hr />
          <div className="center" style={{ fontWeight: 700 }}>
            *** CANCELLED ***
          </div>
        </>
      )}
      <hr />
      <div className="center">{footer || 'Thank you! Visit again.'}</div>
    </div>
  );
}
